// ============================================================================
// Dialysis chemical stock — words and numbers.
//
// ⛔ LAUNCH-DAY RULES (Ben, 2026-09-23: "Ensure that things do not break before
// we have data"). Every helper here takes a possibly-missing number and returns
// either the number or the sentence saying what is missing and who supplies it.
// Never 0 for "unknown", never blank, never NaN.
// ============================================================================

import type { ClinicStockRow, LocationStockRow, MovementRow } from './api';

export const DAY_MS = 86_400_000;

export function isNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** Micro-Chem in gallons to one decimal; Stearns in whole tabs. */
export function fmtQty(v: number | null | undefined, unit: string | null | undefined): string {
  if (!isNum(v)) return '—';
  if (unit === 'tabs') return `${Math.round(v).toLocaleString('en-US')} tabs`;
  const r = Math.round(v * 10) / 10;
  return `${r.toLocaleString('en-US', { maximumFractionDigits: 1 })} gal`;
}

export function fmtRateMonth(v: number | null | undefined, unit: string | null | undefined): string {
  if (!isNum(v)) return '—';
  if (unit === 'tabs') return `${Math.round(v)} tabs a month`;
  return `${(Math.round(v * 100) / 100).toLocaleString('en-US')} gal a month`;
}

export function fmtRateDay(v: number | null | undefined, unit: string | null | undefined): string {
  if (!isNum(v)) return '—';
  if (unit === 'tabs') return `${(Math.round(v * 10) / 10).toLocaleString('en-US')} tabs/day`;
  return `${(Math.round(v * 1000) / 1000).toLocaleString('en-US')} gal/day`;
}

export function fmtMoney(v: number | null | undefined, digits = 2): string {
  if (!isNum(v)) return '—';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function fmtDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function fmtDays(v: number | null | undefined): string {
  if (!isNum(v)) return '—';
  const n = Math.floor(v);
  return `${n} day${n === 1 ? '' : 's'}`;
}

/** Suggested amount to leave: gallons to the nearest half, tabs to the nearest 5. */
export function roundLeave(v: number | null | undefined, unit: string | null | undefined): number {
  if (!isNum(v) || v <= 0) return 0;
  if (unit === 'tabs') return Math.max(5, Math.round(v / 5) * 5);
  return Math.max(0.5, Math.round(v * 2) / 2);
}

export function countSourceLabel(src: string | null | undefined): string {
  switch (src) {
    case 'self':
      return 'counted';
    case 'reported':
      return 'reported by the night manager or cleaner';
    case 'inspection':
      return 'during an inspection';
    case 'at_delivery':
      return 'at delivery';
    default:
      return '';
  }
}

export function rateSourceLabel(row: Pick<ClinicStockRow, 'rate_source' | 'n_obs'>): string {
  switch (row.rate_source) {
    case 'fixed':
      return 'set by hand';
    case 'estimate':
      return 'estimated';
    case 'blended':
      return `learning (${row.n_obs} count${row.n_obs === 1 ? '' : 's'} so far)`;
    case 'learned':
      return 'learned from counts';
    default:
      return 'estimated';
  }
}

/** "Chairs needed, using average" / "nights needed, using 6". */
export function needsDataLabel(row: Pick<ClinicStockRow, 'chairs_missing' | 'nights_missing'>): string | null {
  const parts: string[] = [];
  if (row.chairs_missing) parts.push('chairs needed, using average');
  if (row.nights_missing) parts.push('nights needed, using 6');
  return parts.length ? parts.join(' · ') : null;
}

/** The facility's cleaner, from the database's own display rule "Name (cleaner)". */
export function cleanerOf(display: string | null | undefined, name: string | null | undefined): string | null {
  if (!display || !name) return null;
  if (display.startsWith(`${name} (`) && display.endsWith(')')) return display.slice(name.length + 2, -1);
  return null;
}

/** "Why this number?" — the arithmetic in words. */
export function whyThisNumber(row: ClinicStockRow): string[] {
  const u = row.unit_label;
  const lines: string[] = [];
  if (!row.has_shelf) {
    lines.push('No shelf level yet. The first delivery or count sets it.');
  } else {
    const since = row.last_event_at ? Math.max(0, (Date.now() - new Date(row.last_event_at).getTime()) / DAY_MS) : 0;
    const base = row.last_count_id
      ? `${fmtQty(row.last_count_qty, u)} counted on ${fmtDay(row.last_count_at)} (${countSourceLabel(row.last_count_source)})`
      : 'nothing counted yet, so the shelf starts from what was delivered';
    const net = isNum(row.shelf_at_last_event) && isNum(row.last_count_qty) ? row.shelf_at_last_event - row.last_count_qty : row.shelf_at_last_event;
    const plus = isNum(net) && Math.abs(net) > 0.0001 ? `, plus ${fmtQty(net, u)} delivered since` : '';
    lines.push(
      `${base}${plus}, minus ${Math.round(since)} day${Math.round(since) === 1 ? '' : 's'} × ${fmtRateDay(row.rate_day, u)}.`,
    );
  }
  if (row.rate_source === 'fixed') {
    lines.push(
      `${fmtRateMonth(row.fixed_per_month, u)} was set by hand${row.fixed_until ? `, until ${fmtDay(row.fixed_until)}` : ''}.` +
        (row.check_setting ? ` The counts now say ${fmtRateMonth(row.auto_month, u)} — check your setting.` : ''),
    );
  } else if (row.rate_source === 'estimate') {
    lines.push(
      `${fmtRateMonth(row.rate_month, u)} is a starting estimate: ${fmtNum(row.chairs_used)} chairs × ${fmtNum(row.nights_used)} nights a week × the company rate. It is replaced as real counts come in.`,
    );
  } else {
    lines.push(
      `${fmtRateMonth(row.rate_month, u)} is ${rateSourceLabel(row)}. Company estimate for a clinic this size: ${fmtRateMonth(row.company_est_month, u)}.`,
    );
  }
  const nd = needsDataLabel(row);
  if (nd) lines.push(`Needs data: ${nd}.`);
  return lines;
}

function fmtNum(v: number | null | undefined): string {
  if (!isNum(v)) return '—';
  return (Math.round(v * 10) / 10).toLocaleString('en-US');
}

/** A location's stock line, or what is missing and who supplies it. */
export function locationMissing(row: LocationStockRow): string | null {
  if (!row.has_stock_record && (row.on_order ?? 0) <= 0) {
    return 'No stock recorded yet. It appears when a purchase shipped here is recorded, once it has arrived.';
  }
  if (row.lead_time_days == null) return 'Needs a lead time — the first purchase’s expected arrival sets it.';
  if ((row.use_per_day ?? 0) <= 0) return 'No clinics draw from here yet.';
  return null;
}

export function movementPhrase(m: MovementRow, names: Record<string, { name: string }>, offices: Record<string, string>): string {
  const who = m.recorded_by_eid ? names[m.recorded_by_eid]?.name ?? m.recorded_by_eid : 'System';
  const u = m.supply_id === 'SUP-0230' ? 'tabs' : 'gal';
  const q = fmtQty(m.qty, u);
  const at = (id: string | null) => (id ? offices[id] ?? id : '');
  switch (m.kind) {
    case 'delivery':
      return `${who} left ${q} from ${at(m.office_id)}`;
    case 'count': {
      const src = m.count_source === 'reported'
        ? `, reported by ${m.reported_by_name ?? 'the night manager or cleaner'}`
        : m.count_source ? ` ${countSourceLabel(m.count_source)}` : '';
      return `${who} counted ${q}${src}`;
    }
    case 'receipt':
      return `${who} received ${q} at ${at(m.office_id)}`;
    case 'transfer':
      return `${who} moved ${q} from ${at(m.office_id)} to ${at(m.to_office_id)}`;
    case 'pickup':
      return `${who} picked up ${q}, back to ${at(m.office_id)}`;
    default:
      return `${who}: ${q}`;
  }
}
